import { CompactionTab } from "@/components/chat/chat-debug/compaction-tab";
import {
  toContextGap,
  toSegmentRows,
} from "@/components/chat/chat-debug/context-segments";
import { ContextTab } from "@/components/chat/chat-debug/context-tab";
import { collectIncidents } from "@/components/chat/chat-debug/incidents";
import { IncidentsTab } from "@/components/chat/chat-debug/incidents-tab";
import { MessagesTab } from "@/components/chat/chat-debug/messages-tab";
import { buildDebugReport } from "@/components/chat/chat-debug/report";
import { ToolsTab } from "@/components/chat/chat-debug/tools-tab";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToastError } from "@/components/ui/use-toast";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useTranslation } from "@/hooks/useTranslation";
import type {
  ContextWindowUsage,
  ConversationDebugDTO,
  ConversationMessageMetadata,
  LLMModels,
} from "@shaarp/common";
import type { UIMessage } from "ai";
import { Check, Copy, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

type ChatDebugSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  /** Context window of the last answered turn, as the API persisted it. */
  usage: ContextWindowUsage;
  llmId?: LLMModels;
  /** The messages the browser is holding, for incidents the API never stores. */
  messages: UIMessage<ConversationMessageMetadata>[];
};

const COPIED_FEEDBACK_MS = 2000;

/**
 * Everything about the current conversation that the chat itself does not show:
 * how the window is spent, every compaction and its summary, the turns that were
 * cut or errored, and what the tools remember.
 *
 * Admin-only. The interface goes through `t()` like the other debug surfaces of
 * this codebase (`search-engine-debug-modal`, `company-debug-modal`); only the
 * Markdown report stays hardcoded, as theirs does — its reader is an agent, not
 * a user.
 *
 * Two sources, kept apart on purpose. The API read is the durable one and covers
 * the whole conversation, however long ago. The browser's own message list is
 * the only place chat errors exist at all — they are never persisted — so they
 * are labelled as belonging to this session and disappear on reload.
 *
 * This component only fetches, arranges the tabs and exports; every panel and
 * every derivation lives in `chat-debug/`.
 */
export function ChatDebugSheet({
  open,
  onOpenChange,
  conversationId,
  usage,
  llmId,
  messages,
}: ChatDebugSheetProps) {
  const { t } = useTranslation();
  const toastError = useToastError();
  const [copied, setCopied] = useState(false);

  // The ring is shown on the team-role `admin` axis (`ToggleAdminMode`) while the
  // route is gated on the platform `adminLevel` axis (`AdminGuard`) — the same
  // split the search-engine debug modal lives with. A team admin who is not a
  // platform admin therefore gets a 403 here, so it is spelled out rather than
  // surfaced as a bare "Forbidden".
  const {
    data: debug,
    isLoading,
    isError,
    error,
  } = useApiQuery<ConversationDebugDTO>({
    key: ["conversation-debug", conversationId],
    path: `/conversation/${conversationId}/debug`,
    // Nothing is fetched until the panel is opened: this is a diagnostic, and it
    // reads every message of the conversation.
    enabled: open && conversationId.length > 0,
    staleTime: 0,
  });

  const rows = useMemo(() => toSegmentRows(usage.segments), [usage.segments]);
  const gap = useMemo(() => toContextGap(usage), [usage]);
  const incidents = useMemo(
    () => collectIncidents(debug?.messages, messages),
    [debug?.messages, messages]
  );

  // Closing the sheet unmounts it well within the two seconds the "copied"
  // label lasts, so the timer has to be cancellable.
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(
    () => () => {
      clearTimeout(copiedTimerRef.current);
    },
    []
  );

  /**
   * Built on the click, never memoised.
   *
   * Radix unmounts the sheet's content but not this component, and react-query
   * keeps `debug` for the rest of the session once the panel has been opened
   * once. A `useMemo` keyed on `messages` therefore rebuilt the whole report —
   * every message of the conversation, every compaction summary in full — on
   * every streamed token, behind a closed panel. The report has exactly one
   * consumer, and it is this button.
   */
  const copyReport = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(
        buildDebugReport({
          conversationId,
          debug,
          usage,
          llmId,
          rows,
          gap,
          incidents,
        })
      );
    } catch {
      // No clipboard outside a secure context, and none on a refused
      // permission. Without this the button silently did nothing and the
      // rejection went unhandled.
      toastError({
        title: t("chat.debug.copyFailed.title"),
        description: t("chat.debug.copyFailed.description"),
      });
      return;
    }

    setCopied(true);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(
      () => setCopied(false),
      COPIED_FEEDBACK_MS
    );
  };

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent
        className="flex w-full flex-col gap-0 overflow-hidden sm:max-w-2xl"
        side="right"
      >
        <SheetHeader className="shrink-0">
          <SheetTitle className="flex items-center gap-2">
            {t("chat.debug.title")}
            <Badge variant="outline">{t("chat.debug.adminBadge")}</Badge>
          </SheetTitle>
        </SheetHeader>

        <Tabs className="flex min-h-0 flex-1 flex-col" defaultValue="context">
          <TabsList className="mx-4 mt-2 shrink-0">
            <TabsTrigger value="context">
              {t("chat.debug.tabs.context")}
            </TabsTrigger>
            <TabsTrigger value="compaction">
              {t("chat.debug.tabs.compaction")}
              <TabCount count={debug?.compactions.length} variant="secondary" />
            </TabsTrigger>
            <TabsTrigger value="incidents">
              {t("chat.debug.tabs.incidents")}
              <TabCount count={incidents.length} variant="destructive" />
            </TabsTrigger>
            <TabsTrigger value="tools">{t("chat.debug.tabs.tools")}</TabsTrigger>
            <TabsTrigger value="messages">
              {t("chat.debug.tabs.messages")}
            </TabsTrigger>
          </TabsList>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            {isError && <ReadFailure error={error} />}

            <TabsContent className="mt-0" value="context">
              <ContextTab gap={gap} llmId={llmId} rows={rows} usage={usage} />
            </TabsContent>
            <TabsContent className="mt-0" value="compaction">
              <CompactionTab debug={debug} isLoading={isLoading} />
            </TabsContent>
            <TabsContent className="mt-0" value="incidents">
              <IncidentsTab incidents={incidents} isLoading={isLoading} />
            </TabsContent>
            <TabsContent className="mt-0" value="tools">
              <ToolsTab debug={debug} isLoading={isLoading} />
            </TabsContent>
            <TabsContent className="mt-0" value="messages">
              <MessagesTab debug={debug} isLoading={isLoading} />
            </TabsContent>
          </div>

          <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-secondary px-4 py-3">
            <p className="text-muted-foreground text-xs">
              {t("chat.debug.copyHint")}
            </p>
            <Button
              className="gap-2"
              onClick={copyReport}
              size="sm"
              variant="outline"
            >
              {copied ? (
                <Check className="size-3.5" />
              ) : (
                <Copy className="size-3.5" />
              )}
              {t("chat.debug.copy")}
            </Button>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function TabCount({
  count,
  variant,
}: {
  count: number | undefined;
  variant: "secondary" | "destructive";
}) {
  if (!count) return null;

  return (
    <Badge className="ml-1.5" variant={variant}>
      {count}
    </Badge>
  );
}

function ReadFailure({ error }: { error: Error | null }) {
  const { t } = useTranslation();
  const isForbidden =
    (error as { response?: { status?: number } } | null)?.response?.status ===
    403;

  return (
    <p className="mb-4 flex items-start gap-2 text-destructive text-sm">
      <TriangleAlert className="mt-0.5 size-4 shrink-0" />
      {isForbidden
        ? t("chat.debug.errorForbidden")
        : t("chat.debug.errorGeneric", {
            message: error?.message ?? "",
          })}
    </p>
  );
}
